import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import ClearIcon from "@mui/icons-material/Clear";

import { ErrorProvider, AlertsAddressBookContext } from "services/Utils";
import { updateItem as updateItemOp } from "store/operations";

const useStyles = makeStyles((theme) => ({
  detailsContainer: {
    borderRadius: 8,
    borderColor: "lightgray",
    borderWidth: 3,
    borderStyle: "solid",
  },
  dialogPaper: {
    width: "100%",
    height: "100%",
    maxWidth: 960,
  },
  dialogTitleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogTabContents: {
    display: "flex",
    flexDirection: "column",
    paddingTop: 0,
  },
  detailStatusCell: {
    height: "1rem",
    width: "4rem",
  },
  alertActionsHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  detailsActionsHeader: {
    float: "right",
    marginBottom: 12,
  },
  tagsActionsHeader: {
    paddingLeft: 3,
    marginBottom: 12,
  },
  actionButton: {
    marginLeft: 3,
    marginRight: 3,
  },
  alertItemsContainer: {
    height: "100%",
    paddingLeft: 4,
    paddingRight: 12,
    overflowY: "auto",
    borderTop: "1px dotted lightgray",
  },
  statusCell: {
    height: "1em",
    width: "3em",
  },
  propertyNameCell: {
    width: "30%",
    fontWeight: "bold",
  },
  propertyValueCell: {
    width: "70%",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  selectTagPrompt: {
    borderColor: "lightgray",
    borderStyle: "solid",
    borderWidth: 3,
    borderRadius: 8,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    paddingTop: 32,
    marginTop: 12,
    marginRight: 4,
    marginLeft: 4,
    height: 160,
    fontSize: "1.25rem",
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  tagDirtyToast: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
}));

const StackManagementDialog = (props) => {
  const [title, setTitle] = useState("");
  const [dirty, setDirty] = useState(false);
  const [dirtyToastOpen, setDirtyToastOpen] = useState(false);
  const stacks = useSelector((state) => state.isx.stacks);

  const dispatch = useDispatch();
  const classes = useStyles();

  const [sendTo, setSendTo] = useState([]);
  const value = useMemo(() => ({ sendTo, setSendTo }), [sendTo]);
  const {
    action,
    selectedStack,
    dialogOpen,
    closeDialog,
    readOnly,
  } = props;

  const stack = stacks[selectedStack];

  const updateItem = (guuid, contents) => {
    dispatch(updateItemOp(guuid, contents));
  };

  const handleClose = () => {
    if (!dirty) {
      closeDialog();
    } else {
      setDirtyToastOpen(true);
    }
  };

  if (stack) {
    const devSession = JSON.parse(stack.dev_session);
    const label = stack?.name || devSession["stack.label"] || "<Unnamed>";
    return (
      <ErrorProvider>
        <AlertsAddressBookContext.Provider value={value}>
          <Dialog
            open={dialogOpen}
            // onClose={handleClose}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>
              <div className={classes.dialogTitleHeader}>
                {`${label}: ${title}`}
                <IconButton onClick={handleClose}>
                  <ClearIcon />
                </IconButton>
              </div>
            </DialogTitle>
            <DialogContent className={classes.dialogTabContents}>
              {action && (
                <action.component
                  stack={stack}
                  updateItem={updateItem}
                  setTitle={setTitle}
                  dirty={dirty}
                  setDirty={setDirty}
                  readOnly={readOnly}
                />
              )}
            </DialogContent>
            <Snackbar
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              open={dirtyToastOpen}
              autoHideDuration={3000}
              onClose={() => setDirtyToastOpen(false)}
              classes={{
                root: classes.error,
              }}
              message="Updates not saved. Please save or cancel."
            />
          </Dialog>
        </AlertsAddressBookContext.Provider>
      </ErrorProvider>
    );
  } else {
    return null;
  }
};

export default StackManagementDialog;
